import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import LightboxGallery from '../components/LightboxGallery'
import BoatCard from '../components/BoatCard'
import SEO from '../components/SEO'
import { getBoatType, getBoatStatus, getBoatByggemateriale, getBoatBraendstof } from '../lib/utils'

// Helper parsers for technical specifications
function decodeHtmlEntities(str) {
  if (!str) return ''
  return String(str)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&#039;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&aelig;/gi, 'æ')
    .replace(/&AElig;/gi, 'Æ')
    .replace(/&oslash;/gi, 'ø')
    .replace(/&Oslash;/gi, 'Ø')
    .replace(/&aring;/gi, 'å')
    .replace(/&Aring;/gi, 'Å')
    .replace(/&ndash;/gi, '–')
    .replace(/&mdash;/gi, '—')
    .replace(/&bull;/gi, '•')
    .replace(/&trade;/gi, '™')
    .replace(/&reg;/gi, '®')
    .replace(/&copy;/gi, '©')
    .replace(/&eacute;/gi, 'é')
    .replace(/&Eacute;/gi, 'É')
    .replace(/&#(\d+);/g, (match, dec) => {
      try {
        return String.fromCharCode(dec)
      } catch {
        return match
      }
    })
    .replace(/&#x([0-9a-f]+);/gi, (match, hex) => {
      try {
        return String.fromCharCode(parseInt(hex, 16))
      } catch {
        return match
      }
    })
    .replace(/\u00A0/g, ' ')
}

function cleanPlainText(str) {
  if (!str) return ''
  const decoded = decodeHtmlEntities(str)
  const stripped = decoded.replace(/<[^>]*>/g, ' ')
  return stripped.replace(/\s+/g, ' ').trim()
}

function parseSpecs(specHtml) {
  if (!specHtml) return []
  const decoded = decodeHtmlEntities(specHtml)
  const clean = decoded.replace(/<\/?p>/gi, '').trim()
  const lines = clean.split(/<br\s*\/?>|\n/gi)
  const items = []
  lines.forEach((line) => {
    const text = cleanPlainText(line)
    if (!text) return
    const parts = text.split(':')
    if (parts.length >= 2) {
      const label = cleanPlainText(parts[0])
      const value = cleanPlainText(parts.slice(1).join(':'))
      if (label && value) {
        items.push({ label, value })
      }
    } else {
      items.push({ label: 'Specifikation', value: text })
    }
  })
  return items
}

function parseDimensioner(dimStr) {
  if (!dimStr) return []
  const decoded = decodeHtmlEntities(dimStr)
  const parts = decoded.split('|')
  const items = []
  parts.forEach((p) => {
    const clean = cleanPlainText(p)
    if (!clean) return
    const sub = clean.split(':')
    if (sub.length >= 2) {
      const label = cleanPlainText(sub[0])
      const value = cleanPlainText(sub.slice(1).join(':'))
      if (label && value) {
        items.push({ label, value })
      }
    } else {
      items.push({ label: 'Mål', value: clean })
    }
  })
  return items
}

function parseEquipment(udstyrHtml) {
  if (!udstyrHtml) return []
  const decoded = decodeHtmlEntities(udstyrHtml)
  const liMatches = [...decoded.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
  if (liMatches.length > 0) {
    return liMatches
      .map((m) => cleanPlainText(m[1]))
      .filter((s) => s && !s.match(/^(udstyr\s+der\s+medfølger|desuden\s+medfølger|desuden\s+medfølge|medfølgende\s+udstyr|udstyrsliste|ekstraudstyr)/i))
  }
  const cleanWithBreaks = decoded.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, ' ')
  const lines = cleanWithBreaks
    .split('\n')
    .map((s) => cleanPlainText(s).replace(/^[-•*]\s*/, ''))
    .filter(Boolean)
  const items = []
  lines.forEach((line) => {
    if (line.match(/^(udstyr\s+der\s+medfølger|desuden\s+medfølger|desuden\s+medfølge|medfølgende\s+udstyr|udstyrsliste|ekstraudstyr|udstyr)(?::)?$/i)) {
      return
    }
    if (line.includes(',') && !line.includes('(')) {
      line.split(',').forEach((sub) => {
        const c = cleanPlainText(sub).replace(/\.$/, '')
        if (c) items.push(c)
      })
    } else {
      items.push(line)
    }
  })
  return items.filter(Boolean)
}

function getSpecIcon(label = '') {
  const l = label.toLowerCase()
  if (l.includes('skrog') || l.includes('materiale')) return 'fa-solid fa-ship'
  if (l.includes('brændstof') || l.includes('diesel') || l.includes('tank')) return 'fa-solid fa-gas-pump'
  if (l.includes('vand')) return 'fa-solid fa-droplet'
  if (l.includes('kahyt')) return 'fa-solid fa-door-closed'
  if (l.includes('køje') || l.includes('sove')) return 'fa-solid fa-bed'
  if (l.includes('placer') || l.includes('havn')) return 'fa-solid fa-location-dot'
  if (l.includes('kontakt')) return 'fa-solid fa-user-tie'
  if (l.includes('længde')) return 'fa-solid fa-ruler-horizontal'
  if (l.includes('bredde')) return 'fa-solid fa-arrows-left-right'
  if (l.includes('dybgang')) return 'fa-solid fa-water'
  if (l.includes('deplacement') || l.includes('vægt')) return 'fa-solid fa-weight-hanging'
  if (l.includes('motor') || l.includes('hk') || l.includes('timer')) return 'fa-solid fa-gears'
  if (l.includes('årgang') || l.includes('byggeår')) return 'fa-solid fa-calendar-days'
  return 'fa-solid fa-[#2BABE3] fa-circle-info'
}

export default function BoatDetailPage() {
  const { slug } = useParams()
  const [boat, setBoat] = useState(null)
  const [allBoats, setAllBoats] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [copiedLink, setCopiedLink] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false)
  const navigate = useNavigate()

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      let savedQuery = ''
      try {
        savedQuery = sessionStorage.getItem('nordfyn_boats_filters_query') || ''
      } catch (e) {}
      navigate(`/baade${savedQuery}`)
    }
  }

  // Contact form state
  const [formState, setFormState] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  })
  const [formStatus, setFormStatus] = useState({ submitting: false, success: false, error: '' })

  useEffect(() => {
    setLoading(true)
    fetch('https://api.dev.auxo.dk/public/v2/content/baade?limit=100')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          setAllBoats(data.items)
          const targetSlug = decodeURIComponent(slug || '').trim().toLowerCase()
          const found = data.items.find((b) => {
            const bUrlSlug = (b.url_slug || '').trim().toLowerCase()
            const bSysSlug = (typeof b.slug === 'object' ? b.slug?._ : b.slug || '').trim().toLowerCase()
            const bSagsnummer = String(b.sagsnummer || '').trim().toLowerCase()
            const bId = String(b.id || '').trim().toLowerCase()

            return (
              (bUrlSlug && bUrlSlug === targetSlug) ||
              (bSysSlug && bSysSlug === targetSlug) ||
              (bSagsnummer && bSagsnummer === targetSlug) ||
              (bId && bId === targetSlug)
            )
          })
          setBoat(found || null)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 py-20">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[#1E7BC1]" />
          <p className="text-xs text-slate-600 font-medium">Henter båddata...</p>
        </div>
      </div>
    )
  }

  if (!boat) {
    return (
      <div className="min-h-screen bg-slate-50 py-20 px-4">
        <SEO title="Båden blev ikke fundet - Nordfyn Marine" />
        <div className="max-w-md mx-auto text-center bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-amber-500" />
          <h1 className="font-serif font-bold text-2xl text-slate-900">
            Båden blev ikke fundet
          </h1>
          <p className="text-xs text-slate-600">
            Båden kan være solgt eller fjernet. Du kan søge videre i vores aktuelle udvalg.
          </p>
          <Link
            to="/baade"
            className="inline-block bg-[#1E7BC1] text-white px-6 py-2.5 rounded-xl text-xs font-bold hover:bg-[#2BABE3] transition-colors"
          >
            Se alle både til salg
          </Link>
        </div>
      </div>
    )
  }

  // SEO metadata calculation
  const seoTitle = boat.seo_titel || `${boat.title} - Nordfyn Marine`

  // Extract clean description without HTML tags if custom SEO description is not defined
  let rawDesc = boat.seo_description || boat.kort_beskrivelse
  if (!rawDesc && boat.beskrivelse) {
    rawDesc = boat.beskrivelse.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  }
  const seoDesc = rawDesc
    ? rawDesc.length > 160
      ? rawDesc.substring(0, 157) + '...'
      : rawDesc
    : `${boat.title} til salg hos Nordfyn Marine. Ring på 40 20 70 34.`

  // Robustly extract main boat image (first image) for gallery, social sharing and OG image tag
  let mainImg = null
  if (Array.isArray(boat.billede) && boat.billede.length > 0 && boat.billede[0]) {
    mainImg = boat.billede[0]
  } else if (typeof boat.billede === 'string' && boat.billede.trim()) {
    mainImg = boat.billede.trim()
  } else if (Array.isArray(boat.galleri) && boat.galleri.length > 0 && boat.galleri[0]) {
    mainImg = boat.galleri[0]
  } else if (typeof boat.galleri === 'string' && boat.galleri.trim()) {
    mainImg = boat.galleri.trim()
  } else if (Array.isArray(boat.billeder) && boat.billeder.length > 0 && boat.billeder[0]) {
    mainImg = boat.billeder[0]
  }

  const extraImgs = Array.isArray(boat.galleri) ? boat.galleri : []
  const galleryImages = [mainImg, ...extraImgs].filter(
    (img, index, self) => img && typeof img === 'string' && self.indexOf(img) === index
  )

  const handleCopyLink = () => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setCopiedLink(true)
          setTimeout(() => setCopiedLink(false), 3000)
        })
        .catch(() => {})
    }
  }

  const handleShareClick = () => {
    // If mobile native share is supported, try it, or open our detailed share modal
    if (typeof navigator !== 'undefined' && navigator.share && window.innerWidth < 768) {
      navigator
        .share({
          title: boat?.title || 'Båd til salg hos Nordfyn Marine',
          text: seoDesc,
          url: window.location.href
        })
        .catch(() => {
          setShareModalOpen(true)
        })
    } else {
      setShareModalOpen(true)
    }
  }

  const openLightbox = (idx) => {
    setActiveImageIndex(idx)
    setLightboxOpen(true)
  }

  const boatType = getBoatType(boat)
  const boatStatus = getBoatStatus(boat)
  const boatByggemateriale = getBoatByggemateriale(boat)
  const boatBraendstof = getBoatBraendstof(boat)

  // Related boats
  const relatedBoats = allBoats
    .filter((b) => b.id !== boat.id && getBoatType(b) === boatType)
    .slice(0, 3)

  // Categorize specs into Båddata, Motorspecifikationer & Dynamic Extra Specs
  const parsedDims = parseDimensioner(boat.dimensioner)
  const parsedSpecs = parseSpecs(boat.specifikationer)

  // Map parsed specs into a quick lookup dictionary for fallbacks
  const fallbackSpecMap = {}
  parsedSpecs.forEach((s) => {
    if (s.label && s.value) {
      fallbackSpecMap[s.label.toLowerCase().trim()] = s.value.trim()
    }
  })
  parsedDims.forEach((d) => {
    if (d.label && d.value) {
      fallbackSpecMap[d.label.toLowerCase().trim()] = d.value.trim()
    }
  })

  // 1. Båddata Group
  const baddataFieldDefs = [
    { key: 'baddata_aargang', label: 'Årgang', fallbackKeys: ['aargang'] },
    { key: 'baddata_byggenr', label: 'Byggenr', fallbackKeys: ['byggenr', 'bygge nr'] },
    { key: 'baddata_vaerft', label: 'Værft', fallbackKeys: ['værft', 'vaerft'] },
    { key: 'baddata_byggemateriale', label: 'Byggemateriale', fallbackKeys: ['byggemateriale', 'skrog'] },
    { key: 'baddata_laengde', label: 'Længde', fallbackKeys: ['længde'] },
    { key: 'baddata_bredde', label: 'Bredde', fallbackKeys: ['bredde'] },
    { key: 'baddata_dybdegang', label: 'Dybdegang', fallbackKeys: ['dybdegang', 'dybgang'] },
    { key: 'baddata_deplacement', label: 'Deplacement', fallbackKeys: ['deplacement', 'vægt'] },
    { key: 'baddata_koel_form_vaegt', label: 'Køl form/vægt', fallbackKeys: ['køl form/vægt', 'køl form', 'køl'] },
    { key: 'baddata_skrogfarve', label: 'Skrogfarve', fallbackKeys: ['skrogfarve'] },
    { key: 'baddata_daek', label: 'Dæk', fallbackKeys: ['dæk', 'daek', 'dæksfarve', 'dæksmateriale'] },
    { key: 'baddata_antal_koejer', label: 'Antal køjer', fallbackKeys: ['antal køjer', 'køjepladser', 'køjer'] },
    { key: 'baddata_pantry', label: 'Pantry', fallbackKeys: ['pantry'] },
    { key: 'baddata_kogeapparat', label: 'Kogeapparat', fallbackKeys: ['kogeapparat', 'komfur'] },
    { key: 'baddata_vask', label: 'Vask', fallbackKeys: ['vask'] },
    { key: 'baddata_koeleskab', label: 'Køleskab', fallbackKeys: ['køleskab', 'køleboks'] },
    { key: 'baddata_toilet', label: 'Toilet', fallbackKeys: ['toilet', 'søtoilet'] },
    { key: 'baddata_daeksudstyr', label: 'Dæksudstyr', fallbackKeys: ['dæksudstyr', 'dækudstyr', 'dæk udstyr'] },
    { key: 'baddata_diverse', label: 'Diverse', fallbackKeys: ['bådens stand', 'diverse'] },
  ]

  const boatDataRows = []
  if (boat.sagsnummer) boatDataRows.push({ label: 'Sagsnummer', value: boat.sagsnummer })
  // Standard boat metadata if present
  if (boatType) boatDataRows.push({ label: 'Bådtype', value: boatType })
  if (boat.maerke_model) boatDataRows.push({ label: 'Mærke / Model', value: boat.maerke_model })

  baddataFieldDefs.forEach((def) => {
    let val = boat[def.key]
    if (def.key === 'baddata_byggemateriale' && boatByggemateriale) {
      val = boatByggemateriale
    }
    if (!val && def.fallbackKeys) {
      for (const fk of def.fallbackKeys) {
        if (boat[fk]) {
          val = String(boat[fk])
          break
        } else if (fallbackSpecMap[fk]) {
          val = fallbackSpecMap[fk]
          break
        }
      }
    }
    if (val && String(val).trim()) {
      boatDataRows.push({ label: def.label, value: String(val).trim() })
    }
  })

  // 2. Motorspecifikationer Group
  const motorFieldDefs = [
    { key: 'motor_type', label: 'Type', fallbackKeys: ['motortype', 'type', 'motor_info'] },
    { key: 'motor_hk', label: 'HK', fallbackKeys: ['motor hk', 'hk'] },
    { key: 'motor_sejltimer', label: 'Sejltimer', fallbackKeys: ['sejltimer', 'motortimer', 'timer'] },
    { key: 'motor_stand', label: 'Stand', fallbackKeys: ['motorstand', 'stand'] },
    { key: 'motor_braendstof', label: 'Brændstof', fallbackKeys: ['brændstof', 'drivstof'] },
    { key: 'motor_marchfart', label: 'Marchfart', fallbackKeys: ['marchfart'] },
    { key: 'motor_topfart', label: 'Topfart', fallbackKeys: ['topfart'] },
    { key: 'motor_gear', label: 'Gear', fallbackKeys: ['gear'] },
    { key: 'motor_cyl_antal', label: 'Cyl. antal', fallbackKeys: ['cyl. antal', 'cylindre'] },
    { key: 'motor_aargang', label: 'Årgang', fallbackKeys: ['motorårgang'] },
    { key: 'motor_diverse', label: 'Diverse', fallbackKeys: ['motor diverse'] },
  ]

  const motorDataRows = []
  motorFieldDefs.forEach((def) => {
    let val = boat[def.key]
    if (def.key === 'motor_braendstof' && boatBraendstof) {
      val = boatBraendstof
    }
    if (!val && def.fallbackKeys) {
      for (const fk of def.fallbackKeys) {
        if (boat[fk]) {
          val = String(boat[fk])
          break
        } else if (fallbackSpecMap[fk]) {
          val = fallbackSpecMap[fk]
          break
        }
      }
    }
    if (val && String(val).trim()) {
      motorDataRows.push({ label: def.label, value: String(val).trim() })
    }
  })

  // Handle motor_ekstra (dynamic extra motor characteristics key -> value)
  if (boat.motor_ekstra) {
    const extraMotorParsed = parseSpecs(boat.motor_ekstra)
    extraMotorParsed.forEach((item) => {
      if (item.label && item.value && String(item.value).trim()) {
        motorDataRows.push({ label: item.label, value: String(item.value).trim() })
      }
    })
  }

  // 3. Rigg & Sejl Group
  const sailDataRows = []
  if (boat.rigg_og_sejl) {
    sailDataRows.push({ label: 'Rigg / Rig', value: String(boat.rigg_og_sejl).trim() })
  }

  const sailFieldDefs = [
    { key: 'sejl_fok', label: 'Fok', fallbackKeys: ['fok'] },
    { key: 'sejl_mesan', label: 'Mesan', fallbackKeys: ['mesan'] },
    { key: 'sejl_krydsfok', label: 'Krydsfok', fallbackKeys: ['krydsfok'] },
    { key: 'sejl_stormfok', label: 'Stormfok', fallbackKeys: ['stormfok'] },
    { key: 'sejl_storsejl', label: 'Storsejl', fallbackKeys: ['storsejl'] },
    { key: 'sejl_genua1', label: 'Genua I', fallbackKeys: ['genua i', 'genua 1', 'genua i (1)'] },
    { key: 'sejl_genua2', label: 'Genua II', fallbackKeys: ['genua ii', 'genua 2', 'genua ii (2)'] },
    { key: 'sejl_spiler', label: 'Spiler', fallbackKeys: ['spiler'] },
    { key: 'sejl_genakker', label: 'Genakker', fallbackKeys: ['genakker', 'gennakker'] },
    { key: 'sejl_diverse', label: 'Diverse', fallbackKeys: ['sejl diverse', 'diverse sejl'] },
  ]

  sailFieldDefs.forEach((def) => {
    let val = boat[def.key]
    if (!val && def.fallbackKeys) {
      for (const fk of def.fallbackKeys) {
        if (boat[fk]) {
          val = String(boat[fk])
          break
        } else if (fallbackSpecMap[fk]) {
          val = fallbackSpecMap[fk]
          break
        }
      }
    }
    if (val && String(val).trim() && String(val).trim().toUpperCase() !== 'N/A') {
      sailDataRows.push({ label: def.label, value: String(val).trim() })
    }
  })

  // Handle sejl_ekstra (dynamic extra characteristics key -> value)
  if (boat.sejl_ekstra) {
    const extraSailParsed = parseSpecs(boat.sejl_ekstra)
    extraSailParsed.forEach((item) => {
      if (item.label && item.value && String(item.value).trim() && String(item.value).trim().toUpperCase() !== 'N/A') {
        sailDataRows.push({ label: item.label, value: String(item.value).trim() })
      }
    })
  }

  // If sejl_specifikationer multiline text exists, parse lines
  if (boat.sejl_specifikationer) {
    const sailParsed = parseSpecs(boat.sejl_specifikationer)
    sailParsed.forEach((item) => {
      if (item.label && item.value && String(item.value).trim() && String(item.value).trim().toUpperCase() !== 'N/A') {
        const existingIdx = sailDataRows.findIndex((r) => r.label.toLowerCase() === item.label.toLowerCase())
        if (existingIdx >= 0) {
          sailDataRows[existingIdx].value = String(item.value).trim()
        } else {
          sailDataRows.push({ label: item.label, value: String(item.value).trim() })
        }
      }
    })
  }

  // 4. Dynamic Extra Specifications (Ekstra specifikationer / custom CMS fields)
  const extraRows = []
  if (boat.ekstra_specifikationer) {
    const extraParsed = parseSpecs(boat.ekstra_specifikationer)
    extraParsed.forEach((item) => {
      if (item.label && item.value) {
        extraRows.push({ label: item.label, value: item.value })
      }
    })
  }

  // Catch any unmapped keys from specifikationer that aren't already included
  const handledLabels = new Set([
    ...baddataFieldDefs.map((d) => d.label.toLowerCase()),
    ...baddataFieldDefs.flatMap((d) => (d.fallbackKeys || []).map((k) => k.toLowerCase())),
    ...motorFieldDefs.map((d) => d.label.toLowerCase()),
    ...motorFieldDefs.flatMap((d) => (d.fallbackKeys || []).map((k) => k.toLowerCase())),
    ...sailFieldDefs.map((d) => d.label.toLowerCase()),
    ...sailFieldDefs.flatMap((d) => (d.fallbackKeys || []).map((k) => k.toLowerCase())),
    'rigg / rig', 'mesan', 'stormfok', 'storsejl', 'genua i', 'genua ii', 'spiler', 'diverse',
    'bådtype', 'mærke / model', 'sagsnummer'
  ])

  parsedSpecs.forEach((spec) => {
    if (!spec.label || !spec.value) return
    const l = spec.label.toLowerCase().trim()
    if (!handledLabels.has(l) && !l.includes('placering') && !l.includes('kontaktperson')) {
      extraRows.push({ label: spec.label, value: spec.value })
    }
  })

  // Øvrige oplysninger (location, contact)
  const otherDataItems = [
    { label: 'Placering', value: 'Kontakt for info', icon: 'fa-solid fa-location-dot' },
    { label: 'Kontaktperson', value: 'Nordfyn Marine', icon: 'fa-solid fa-user-tie' }
  ]

  // Equipment List items
  const equipmentItems = parseEquipment(boat.udstyrsliste || boat.udstyr_medfoelger || boat.desuden_medfoelger)

  // Helper to convert remote image URL to data URL using backend proxy for guaranteed CORS bypass
  const urlToDataUrl = async (url) => {
    if (!url) return ''
    if (url.startsWith('data:')) {
      return url
    }

    const fetchAsDataUrl = async (targetUrl) => {
      const response = await fetch(targetUrl, {
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store'
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ved hentning af ${targetUrl}`)
      }
      const blob = await response.blob()
      if (!blob.type.startsWith('image/')) {
        throw new Error(`Forkert Content-Type: ${blob.type} fra ${targetUrl}`)
      }
      return await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
            resolve(reader.result)
          } else {
            reject(new Error(`Kunne ikke konvertere ${targetUrl} til data-URL`))
          }
        }
        reader.onerror = () => {
          reject(new Error(`FileReader fejlede for ${targetUrl}`))
        }
        reader.readAsDataURL(blob)
      })
    }

    try {
      // Primary attempt: Use deployed backend serverless image proxy (guaranteed CORS bypass)
      const proxyUrl = `https://api.dev.auxo.dk/fn/image-proxy?url=${encodeURIComponent(url)}`
      return await fetchAsDataUrl(proxyUrl)
    } catch (proxyError) {
      console.warn('Proxy hentning fejlede, forsøger direkte fetch:', { url, proxyError })
      try {
        // Fallback: Direct fetch
        return await fetchAsDataUrl(url)
      } catch (directError) {
        console.error('PDF image conversion failed:', { url, directError })
        return ''
      }
    }
  }

  // Helper to crop image with centered aspect ratio (cover mode) to prevent distortion in html2canvas / PDFs
  const cropImageToCover = (imgElement, targetWidth, targetHeight) => {
    if (!imgElement || !imgElement.naturalWidth || !imgElement.naturalHeight) {
      return null
    }
    const naturalW = imgElement.naturalWidth
    const naturalH = imgElement.naturalHeight
    const targetW = Math.max(Math.round(targetWidth), 10)
    const targetH = Math.max(Math.round(targetHeight), 10)

    const targetRatio = targetW / targetH
    const naturalRatio = naturalW / naturalH

    let sX = 0
    let sY = 0
    let sWidth = naturalW
    let sHeight = naturalH

    if (naturalRatio > targetRatio) {
      // Image is wider than target box: crop left & right (center-crop)
      sWidth = Math.round(naturalH * targetRatio)
      sX = Math.round((naturalW - sWidth) / 2)
    } else {
      // Image is taller than target box: crop top & bottom (center-crop)
      sHeight = Math.round(naturalW / targetRatio)
      sY = Math.round((naturalH - sHeight) / 2)
    }

    // High resolution rendering for crisp PDF output (Retina / print quality)
    const destW = Math.min(Math.max(targetW * 2, 800), 2400)
    const destH = Math.round(destW / targetRatio)

    const canvas = document.createElement('canvas')
    canvas.width = destW
    canvas.height = destH

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(imgElement, sX, sY, sWidth, sHeight, 0, 0, destW, destH)

    return canvas.toDataURL('image/jpeg', 0.95)
  }

  // PDF Document Data Builder
  const buildSalesDocData = (mode = 'print', imgOverrides = null) => {
    if (!boat) return null

    const escapeHtml = (str) => {
      if (!str) return ''
      const cleaned = cleanPlainText(str)
      return String(cleaned)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    }

    const sagsnr = cleanPlainText(boat.sagsnummer || '13592')
    const cleanTitle = (boat.title || 'baad')
      .replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_')
      .replace(/__+/g, '_')
    const fileName = `Salgsopstilling-${sagsnr}-${cleanTitle}.pdf`

    // Extract sections from boat.beskrivelse or separate CMS fields if present
    const rawDescHtml = decodeHtmlEntities(boat.beskrivelse || boat.kort_beskrivelse || '')
    
    // Check separate CMS fields first
    const directUdstyr = boat.udstyr_medfoelger || boat.desuden_medfoelger || boat.udstyrsliste || ''
    const directStand = boat.baadens_stand || boat.stand || ''
    const directBytte = boat.kan_byttes_med || boat.bytte || boat.evt_bytte || ''

    let parsedUdstyr = []
    let parsedStand = ''
    let parsedBytte = ''

    if (directUdstyr) {
      parsedUdstyr = parseEquipment(directUdstyr)
    }

    if (directStand) {
      parsedStand = cleanPlainText(typeof directStand === 'string' ? directStand : '')
    }

    if (directBytte) {
      parsedBytte = cleanPlainText(typeof directBytte === 'string' ? directBytte : '')
    }

    // If equipment is not in a separate field, extract from description
    if (parsedUdstyr.length === 0) {
      const udstyrMatch = rawDescHtml.match(/(?:Udstyr\s+der\s+medfølger|Desuden\s+medfølger|Desuden\s+medfølge|Medfølgende\s+udstyr|Udstyrsliste|Ekstraudstyr)(?::)?[\s\S]*?(?=<p[^>]*><b>|<p[^>]*><strong|Bådens\s+stand|Båd\s+stand|Kan\s+evt\.|Evt\.\s*bytte|$)/i)
      if (udstyrMatch) {
        parsedUdstyr = parseEquipment(udstyrMatch[0])
      }
    }

    // If condition/stand is not in a separate field, extract from description
    if (!parsedStand) {
      const standMatch = rawDescHtml.match(/(?:Bådens\s+stand|Båd\s+stand|Stand)(?::)?([\s\S]*?)(?=<p[^>]*><b>|<p[^>]*><strong|Kan\s+evt\.|Evt\.\s*bytte|$)/i)
      if (standMatch) {
        parsedStand = cleanPlainText(standMatch[1])
      }
    }

    // If trade-in/bytte is not in a separate field, extract from description
    if (!parsedBytte) {
      const bytteMatch = rawDescHtml.match(/(?:Kan\s+evt\.?\s*byttes\s+med|Evt\.?\s*bytte|Bytte)(?::)?([\s\S]*?)$/i)
      if (bytteMatch) {
        parsedBytte = cleanPlainText(bytteMatch[1])
      }
    }

    // Clean Intro description (strip out equipment, stand, and bytte blocks so they do not duplicate in intro)
    let cleanIntroDesc = rawDescHtml
      .replace(/<p[^>]*>(?:<b>|<strong|<strong><b>|<b><strong>)?\s*(?:Udstyr\s+der\s+medfølger|Desuden\s+medfølger|Desuden\s+medfølge|Medfølgende\s+udstyr|Udstyrsliste|Ekstraudstyr)(?::)?[\s\S]*$/i, '')
      .replace(/(?:<b>|<strong|<strong><b>|<b><strong>)?\s*(?:Udstyr\s+der\s+medfølger|Desuden\s+medfølger|Desuden\s+medfølge|Medfølgende\s+udstyr|Udstyrsliste|Ekstraudstyr)(?::)?[\s\S]*$/i, '')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')

    const descParagraphs = cleanIntroDesc
      .split('\n')
      .map((p) => cleanPlainText(p))
      .filter((p) => {
        if (!p) return false
        if (p.match(/^(udstyr\s+der\s+medfølger|desuden\s+medfølger|desuden\s+medfølge|bådens\s+stand|kan\s+evt\.|evt\.\s*bytte)/i)) return false
        return true
      })

    const formattedDescHtml =
      descParagraphs.length > 0
        ? descParagraphs.map((p) => `<p style="margin: 0 0 4px 0; line-height: 1.35;">${escapeHtml(p)}</p>`).join('')
        : `<p style="margin: 0 0 4px 0; line-height: 1.35;">${escapeHtml(boat.title || '')}</p>`

    // Prepare Båddata dictionary
    const badDataDict = {}
    boatDataRows.forEach((r) => {
      badDataDict[r.label.toLowerCase().trim()] = r.value
    })

    const getBadVal = (labels, excludeSubstrings = []) => {
      // 1. Exact match first
      for (const l of labels) {
        const target = l.toLowerCase().trim()
        const exactKey = Object.keys(badDataDict).find((k) => k === target)
        if (exactKey && badDataDict[exactKey]) return badDataDict[exactKey]
      }
      // 2. Partial / substring match (excluding unwanted terms)
      for (const l of labels) {
        const target = l.toLowerCase().trim()
        const foundKey = Object.keys(badDataDict).find((k) => {
          if (!k.includes(target)) return false
          if (excludeSubstrings.some((ex) => k.includes(ex.toLowerCase().trim()))) return false
          return true
        })
        if (foundKey && badDataDict[foundKey]) return badDataDict[foundKey]
      }
      return ''
    }


    const baddataOrderedDefs = [
      { label: 'Bådtype', val: boatType || getBadVal(['bådtype', 'baadtype', 'type', 'kategori']) },
      { label: 'Mærke/Model', val: boat.maerke_model || getBadVal(['mærke/model', 'mærke / model']) },
      { label: 'Årgang', val: boat.aargang || getBadVal(['årgang']) },
      { label: 'Byggenummer', val: getBadVal(['byggenr', 'byggenummer', 'bygge nr']) },
      { label: 'Værft', val: getBadVal(['værft', 'vaerft']) },
      { label: 'Byggemateriale', val: boatByggemateriale || getBadVal(['byggemateriale', 'skrogmateriale', 'skrog']) },
      { label: 'Længde', val: getBadVal(['længde', 'laengde']) },
      { label: 'Bredde', val: getBadVal(['bredde']) },
      { label: 'Dybdegang', val: getBadVal(['dybdegang', 'dybgang']) },
      { label: 'Deplacement', val: getBadVal(['deplacement', 'vægt']) },
      { label: 'Køl form/vægt', val: getBadVal(['køl form', 'køl']) },
      { label: 'Skrogfarve', val: getBadVal(['skrogfarve']) },
      { label: 'Dæk', val: boat.baddata_daek || getBadVal(['dæk', 'daek', 'dæksfarve', 'dæksmateriale'], ['udstyr', 'dæksudstyr', 'daeksudstyr', 'dækudstyr']) },
      { label: 'Antal køjer', val: getBadVal(['køjer', 'antal køjer', 'sovepladser']) },
      { label: 'Pantry', val: getBadVal(['pantry']) },
      { label: 'Kogeapparat', val: getBadVal(['kogeapparat', 'komfur']) },
      { label: 'Vask', val: getBadVal(['vask']) },
      { label: 'Køleskab', val: getBadVal(['køleskab', 'køleboks']) },
      { label: 'Toilet', val: getBadVal(['toilet']) },
    ]

    // Dæksudstyr / Diverse
    let daeksudstyrText = boat.baddata_daeksudstyr || getBadVal(['dæksudstyr', 'dæk udstyr', 'dækudstyr', 'udstyr dæk'])
    if (!daeksudstyrText) {
      const divVal = getBadVal(['diverse'], ['motor', 'båd', 'stand'])
      if (divVal && !divVal.toLowerCase().includes('båd')) {
        daeksudstyrText = divVal
      }
    }

    // Equipment items (Desuden medfølger)
    const eqList = parsedUdstyr.length > 0 
      ? parsedUdstyr 
      : (equipmentItems.length > 0 ? equipmentItems : [])

    // Motor dict
    const motorDataDict = {}
    motorDataRows.forEach((r) => {
      motorDataDict[r.label.toLowerCase().trim()] = r.value
    })

    const getMotorVal = (labels, excludeSubstrings = []) => {
      // 1. Exact match first
      for (const l of labels) {
        const target = l.toLowerCase().trim()
        const exactKey = Object.keys(motorDataDict).find((k) => k === target)
        if (exactKey && motorDataDict[exactKey]) return motorDataDict[exactKey]
      }
      // 2. Partial / substring match
      for (const l of labels) {
        const target = l.toLowerCase().trim()
        const foundKey = Object.keys(motorDataDict).find((k) => {
          if (!k.includes(target)) return false
          if (excludeSubstrings.some((ex) => k.includes(ex.toLowerCase().trim()))) return false
          return true
        })
        if (foundKey && motorDataDict[foundKey]) return motorDataDict[foundKey]
      }
      return ''
    }

    const motorOrderedDefs = [
      { label: 'Type', val: getMotorVal(['type', 'motortype']) },
      { label: 'HK', val: getMotorVal(['hk', 'motor hk']) },
      { label: 'Sejltimer', val: getMotorVal(['sejltimer', 'timer']) },
      { label: 'Stand', val: getMotorVal(['stand']) },
      { label: 'Brændstof', val: getMotorVal(['brændstof', 'drivstof']) },
      { label: 'Marchfart', val: getMotorVal(['marchfart']) },
      { label: 'Topfart', val: getMotorVal(['topfart']) },
      { label: 'Gear', val: getMotorVal(['gear']) || boat.motor_gear || '' },
      { label: 'Cyl. Antal', val: getMotorVal(['cyl', 'cyl. antal', 'cylindre']) },
      { label: 'Årgang', val: getMotorVal(['årgang', 'motorårgang']) || boat.motor_aargang || '' },
      { label: 'Diverse', val: getMotorVal(['diverse', 'motor diverse']) || boat.motor_diverse || '' },
    ]

    // Page 2 Specs / Stand & Bytte
    const baadStand = parsedStand || boat.baadens_stand || boat.stand || getBadVal(['stand', 'bådens stand', 'diverse']) || 'God stand'
    const baadBytte = parsedBytte || boat.kan_byttes_med || boat.bytte || boat.evt_bytte || 'Ingen bytte'

    // Gallery images split
    const mainPhoto = imgOverrides?.mainPhoto ?? (galleryImages[0] || '')
    const rawExtraPhotos = galleryImages.slice(1)

    // Distribute extra photos between page 2 and page 3 depending on total photos
    let page2Photos = []
    let page3Photos = []
    let hasPage3 = false

    if (imgOverrides?.page2Photos) {
      page2Photos = imgOverrides.page2Photos
      page3Photos = imgOverrides.page3Photos || []
      hasPage3 = page3Photos.length > 0
    } else if (rawExtraPhotos.length <= 4) {
      page2Photos = rawExtraPhotos
      page3Photos = []
      hasPage3 = false
    } else {
      page2Photos = rawExtraPhotos.slice(0, 4)
      page3Photos = rawExtraPhotos.slice(4, 8)
      hasPage3 = true
    }

    const page2LeftPhotos = page2Photos.filter((_, i) => i % 2 === 0)
    const page2RightPhotos = page2Photos.filter((_, i) => i % 2 === 1)

    const page3LeftPhotos = page3Photos.filter((_, i) => i % 2 === 0)
    const page3RightPhotos = page3Photos.filter((_, i) => i % 2 === 1)

    const logoUrl = imgOverrides?.logoUrl || 'https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/nordfyn-marine-logo.png'

    const renderGalleryHtml = (photos, hasAd) => {
      if (!photos || photos.length === 0) return ''
      const count = Math.min(photos.length, 4)
      const wrapperClass = hasAd ? 'gallery-grid-with-ad' : 'gallery-grid-full'

      if (count === 1) {
        return `
          <div class="gallery-wrapper ${wrapperClass}">
            <div class="gallery-grid-1">
              <div class="gallery-image-wrapper">
                <img src="${photos[0]}" class="gallery-img" alt="" />
              </div>
            </div>
          </div>
        `
      }
      if (count === 2) {
        return `
          <div class="gallery-wrapper ${wrapperClass}">
            <div class="gallery-grid-2">
              <div class="gallery-image-wrapper">
                <img src="${photos[0]}" class="gallery-img" alt="" />
              </div>
              <div class="gallery-image-wrapper">
                <img src="${photos[1]}" class="gallery-img" alt="" />
              </div>
            </div>
          </div>
        `
      }
      if (count === 3) {
        return `
          <div class="gallery-wrapper ${wrapperClass}">
            <div class="gallery-grid-3">
              <div class="gallery-span-2">
                <div class="gallery-image-wrapper">
                  <img src="${photos[0]}" class="gallery-img" alt="" />
                </div>
              </div>
              <div>
                <div class="gallery-image-wrapper">
                  <img src="${photos[1]}" class="gallery-img" alt="" />
                </div>
              </div>
              <div>
                <div class="gallery-image-wrapper">
                  <img src="${photos[2]}" class="gallery-img" alt="" />
                </div>
              </div>
            </div>
          </div>
        `
      }
      // 4 photos
      return `
        <div class="gallery-wrapper ${wrapperClass}">
          <div class="gallery-grid-4">
            <div class="gallery-image-wrapper">
              <img src="${photos[0]}" class="gallery-img" alt="" />
            </div>
            <div class="gallery-image-wrapper">
              <img src="${photos[1]}" class="gallery-img" alt="" />
            </div>
            <div class="gallery-image-wrapper">
              <img src="${photos[2]}" class="gallery-img" alt="" />
            </div>
            <div class="gallery-image-wrapper">
              <img src="${photos[3]}" class="gallery-img" alt="" />
            </div>
          </div>
        </div>
      `
    }

    const stylesCss = `
    @page {
      size: A4 portrait;
      margin: 0;
    }

    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    html, body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a;
      background-color: #f1f5f9;
      margin: 0;
      padding: 0;
      font-size: 9px;
      line-height: 1.35;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    .toolbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #071320;
      color: #ffffff;
      padding: 10px 20px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      z-index: 9999;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    .toolbar-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .toolbar-title {
      font-weight: 700;
      color: #ffffff;
      font-size: 13.5px;
      letter-spacing: 0.2px;
    }

    .toolbar-hint {
      color: #93c5fd;
      font-size: 11px;
    }

    .toolbar-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .btn-print {
      background: #1E7BC1;
      color: #ffffff;
      border: none;
      padding: 7px 16px;
      font-weight: 600;
      font-size: 12px;
      border-radius: 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: background 0.15s ease;
    }

    .btn-print:hover {
      background: #2BABE3;
    }

    .btn-close {
      background: rgba(255,255,255,0.12);
      color: #ffffff;
      border: 1px solid rgba(255,255,255,0.2);
      padding: 7px 12px;
      font-weight: normal;
      font-size: 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .btn-close:hover {
      background: rgba(255,255,255,0.22);
    }

    .document-container {
      width: 210mm;
      margin: 54px auto 24px auto;
      box-sizing: border-box;
    }

    .page {
      width: 210mm;
      height: 297mm;
      max-height: 297mm;
      padding: 24px 28px;
      box-sizing: border-box;
      overflow: hidden;
      position: relative;
      background: #ffffff;
      margin-bottom: 24px;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.06);
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
      break-inside: avoid;
      display: flex;
      flex-direction: column;
    }

    .page-last {
      margin-bottom: 0;
      page-break-after: avoid;
      break-after: avoid;
    }

    @media print {
      .toolbar { display: none !important; }
      html, body {
        padding: 0 !important;
        margin: 0 !important;
        background: #ffffff !important;
        width: 210mm !important;
      }
      .document-container {
        margin: 0 !important;
        padding: 0 !important;
        width: 210mm !important;
        max-width: 210mm !important;
      }
      .page {
        margin: 0 !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        width: 210mm !important;
        height: 297mm !important;
        max-height: 297mm !important;
        padding: 8mm 10mm !important;
        box-sizing: border-box !important;
        page-break-after: always !important;
        break-after: page !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .page-last {
        page-break-after: avoid !important;
        break-after: avoid !important;
      }
      .gallery-wrapper {
        flex: 1 !important;
        height: auto !important;
        min-height: 0 !important;
      }
    }

    /* Outer Header Box */
    .header-box {
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      border-radius: 4px;
      padding: 10px 16px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      min-height: 56px;
    }

    .header-logo-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .header-logo {
      height: 36px;
      width: auto;
      max-width: 140px;
      object-fit: contain;
      display: block;
    }

    .header-title-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 0 12px;
      height: 100%;
    }

    .header-main-title {
      font-size: 15px;
      font-weight: 700;
      margin: 0;
      padding: 0;
      color: #022046;
      line-height: 1.2;
      letter-spacing: 0.2px;
    }

    .header-subtitle {
      font-size: 9.5px;
      font-weight: 500;
      margin: 3px 0 0 0;
      padding: 0;
      color: #1E7BC1;
      line-height: 1.2;
    }

    /* Main Grid Table (Side 1) */
    .grid-table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      table-layout: fixed;
      background: #ffffff;
      margin-bottom: 10px;
    }

    .grid-table > tbody > tr > td {
      vertical-align: top;
      padding: 10px 12px;
      border: 1px solid #e2e8f0;
      box-sizing: border-box;
    }

    .col-left {
      width: 50%;
      border-right: 1px solid #cbd5e1 !important;
    }

    .col-right {
      width: 50%;
    }

    .row-divider > td {
      border-top: 1px solid #cbd5e1 !important;
    }

    .section-block {
      margin-bottom: 8px;
    }

    .section-block:last-child {
      margin-bottom: 0;
    }

    .boat-title-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 6px;
    }

    .boat-title-text {
      font-size: 14.5px;
      font-weight: 700;
      color: #022046;
      line-height: 1.2;
    }

    .case-no-text {
      font-size: 10px;
      font-weight: 600;
      color: #64748b;
      white-space: nowrap;
    }

    .main-image-wrapper {
      width: 100%;
      height: 180px;
      max-height: 190px;
      overflow: hidden;
      margin: 0 0 8px 0;
      border: 1px solid #cbd5e1;
      border-radius: 3px;
      background: #f8fafc;
      box-sizing: border-box;
      position: relative;
      display: block;
    }

    .main-image-wrapper img,
    .main-boat-img {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      object-fit: cover !important;
      object-position: center !important;
      display: block !important;
      border: none !important;
      border-radius: 0 !important;
      margin: 0 !important;
    }

    .price-box {
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      border-radius: 3px;
      min-height: 38px;
      padding: 0 12px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      box-sizing: border-box;
    }

    .price-text {
      font-size: 14.5px;
      font-weight: 700;
      color: #022046;
      line-height: 1.2;
      margin: 0;
      padding: 0;
    }

    .section-title {
      font-size: 10.5px;
      font-weight: 700;
      color: #022046;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin: 0 0 6px 0;
      padding-bottom: 3px;
      border-bottom: 1px solid #e2e8f0;
    }

    /* Key-Value Tables */
    .document-container table {
      border-collapse: collapse;
      margin: 0;
      padding: 0;
    }

    .document-container td,
    .document-container th {
      border: none;
      padding: 0;
    }

    .kv-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5px;
      line-height: 1.35;
      margin: 0;
    }

    .kv-table tr {
      border-bottom: 1px solid #f1f5f9;
    }

    .kv-table tr:last-child {
      border-bottom: none;
    }

    .kv-table td {
      padding: 3px 0;
      vertical-align: top;
      border: none;
      line-height: 1.35;
    }

    .kv-label {
      font-weight: 600;
      width: 40%;
      color: #475569;
      padding-right: 8px !important;
    }

    .kv-val {
      font-weight: 500;
      width: 60%;
      color: #0f172a;
    }

    .bullet-list {
      margin: 0;
      padding-left: 14px;
      font-size: 8.5px;
      line-height: 1.35;
      list-style-type: disc;
      color: #334155;
    }

    .bullet-list li {
      margin-bottom: 2px;
      padding: 1px 0;
      line-height: 1.35;
      border-bottom: 1px solid #f8fafc;
    }

    .bullet-list li:last-child {
      border: none;
      margin-bottom: 0;
    }

    /* Bottom Info Bar / Footer (Side 1 & 2) */
    .info-bar,
    .footer-box {
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      border-radius: 3px;
      padding: 8px 12px;
      margin: 10px 0 0 0;
      min-height: 38px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      font-size: 8px;
      line-height: 1.3;
      box-sizing: border-box;
    }

    .info-bar-top {
      font-weight: 600;
      color: #475569;
      margin: 0;
      padding: 0;
    }

    .info-bar-sub {
      color: #1E7BC1;
      font-weight: 500;
      margin: 2px 0 0 0;
      padding: 0;
    }

    /* Gallery Adaptive Grids (Side 2 & 3) */
    .gallery-wrapper {
      width: 100%;
      box-sizing: border-box;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      padding: 10px;
      display: flex;
      flex-direction: column;
    }

    .gallery-grid-full {
      height: 940px;
    }

    .gallery-grid-with-ad {
      height: 800px;
    }

    .gallery-grid-1 {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 1;
    }

    .gallery-grid-1 .gallery-image-wrapper {
      width: 100%;
      height: 100%;
      max-height: 520px;
    }

    .gallery-grid-2 {
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 10px;
      flex: 1;
    }

    .gallery-grid-3 {
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1.15fr 1fr;
      gap: 10px;
      flex: 1;
    }

    .gallery-span-2 {
      grid-column: span 2;
      width: 100%;
      height: 100%;
    }

    .gallery-grid-4 {
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 10px;
      flex: 1;
    }

    .gallery-image-wrapper {
      width: 100%;
      height: 100%;
      overflow: hidden;
      border: 1px solid #cbd5e1;
      border-radius: 3px;
      background: #ffffff;
      box-sizing: border-box;
      position: relative;
      display: block;
    }

    .gallery-image-wrapper img,
    .gallery-img {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      object-fit: cover !important;
      object-position: center !important;
      display: block !important;
      border: none !important;
      border-radius: 0 !important;
    }

    /* Bottom Sales Ad CTA Box (Last Page) */
    .sales-ad-box {
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      border-radius: 4px;
      padding: 12px 16px;
      margin-top: 12px;
      box-sizing: border-box;
      page-break-inside: avoid;
      break-inside: avoid;
      min-height: 100px;
    }

    .sales-ad-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      height: 100%;
    }

    .sales-ad-left {
      width: 54%;
      vertical-align: middle;
      padding-right: 14px;
    }

    .sales-ad-right {
      width: 46%;
      vertical-align: middle;
      padding-left: 14px;
      border-left: 1px solid #cbd5e1;
    }

    .ad-left-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
    }

    .ad-title {
      font-size: 13.5px;
      font-weight: 700;
      color: #022046;
      line-height: 1.25;
      margin-bottom: 6px;
    }

    .ad-bullets {
      font-size: 9.5px;
      color: #334155;
      line-height: 1.45;
    }

    .ad-bullet-item {
      display: flex;
      align-items: center;
      gap: 5px;
      margin-bottom: 2px;
    }

    .ad-bullet-dot {
      color: #1E7BC1;
      font-weight: 700;
      font-size: 10px;
    }

    .ad-graphic {
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      padding: 10px 14px;
      text-align: center;
      background-color: #ffffff;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
    }

    .ad-graphic-logo {
      height: 24px;
      width: auto;
      margin: 0 auto 4px auto;
      display: block;
      object-fit: contain;
    }

    .ad-graphic-title {
      font-size: 11px;
      font-weight: 800;
      color: #022046;
      margin-bottom: 4px;
      letter-spacing: 0.4px;
      line-height: 1.2;
    }

    .ad-graphic-badge {
      background: #1E7BC1;
      color: #ffffff;
      font-weight: 700;
      font-size: 10px;
      padding: 3px 10px;
      border-radius: 3px;
      display: inline-block;
      margin-bottom: 4px;
      letter-spacing: 0.4px;
      line-height: 1.2;
    }

    .ad-graphic-phone {
      font-size: 10px;
      font-weight: 700;
      color: #022046;
      line-height: 1.2;
      margin: 0;
      padding: 0;
    }
    `

    const rawPagesHtml = `
    <!-- ================================================== -->
    <!-- SIDE 1 -->
    <!-- ================================================== -->
    <div class="page">
      <!-- Header -->
      <div class="header-box">
        <div class="header-logo-container">
          <img src="${logoUrl}" class="header-logo" alt="Nordfyn Marine" />
        </div>
        <div class="header-title-container">
          <h1 class="header-main-title">Salgsopstilling fra Nordfyn Marine</h1>
          <div class="header-subtitle">Ring og aftal tid for besigtigelse på telefon 40 20 70 34 – svarer altid.</div>
        </div>
      </div>

      <!-- Main Area (2-column table) -->
      <table class="grid-table">
        <tbody>
          <tr>
            <!-- Left Column Page 1 -->
            <td class="col-left">
              <div class="section-block">
                <div class="boat-title-row">
                  <span class="boat-title-text">${escapeHtml(boat.title)}</span>
                  <span class="case-no-text">Sagsnr. ${escapeHtml(sagsnr)}</span>
                </div>
              </div>

              ${mainPhoto ? `
                <div class="main-image-wrapper">
                  <img src="${mainPhoto}" class="main-boat-img" alt="${escapeHtml(boat.title)}" />
                </div>
              ` : ''}

              <div class="price-box">
                <span class="price-text">Pris: ${escapeHtml(boat.pris || 'Kontakt for pris')}</span>
              </div>

              <div class="section-block">
                <div class="section-title">Beskrivelse</div>
                <div style="font-size: 9px; line-height: 1.45; color: #334155;">
                  ${formattedDescHtml}
                </div>
              </div>
            </td>

            <!-- Right Column Page 1 -->
            <td class="col-right">
              <div class="section-block">
                <div class="section-title">Båddata</div>
                <table class="kv-table">
                  <tbody>
                    ${baddataOrderedDefs
                      .filter((d) => d.val && String(d.val).trim())
                      .map(
                        (d) => `
                      <tr>
                        <td class="kv-label">${escapeHtml(d.label)}</td>
                        <td class="kv-val">${escapeHtml(d.val)}</td>
                      </tr>
                    `
                      )
                      .join('')}
                  </tbody>
                </table>
              </div>

              ${
                daeksudstyrText
                  ? `
                <div class="section-block">
                  <div class="section-title">Dækudstyr</div>
                  <div style="font-size: 8.5px; line-height: 1.35; color: #334155;">${escapeHtml(daeksudstyrText)}</div>
                </div>
              `
                  : ''
              }
            </td>
          </tr>

          <!-- Bottom Area Page 1: Equipment & Motor -->
          <tr class="row-divider">
            <td class="col-left">
              <div class="section-block">
                <div class="section-title">Udstyr der medfølger</div>
                ${
                  eqList.length > 0
                    ? `
                  <ul class="bullet-list">
                    ${eqList.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
                  </ul>
                `
                    : '<div style="font-size: 8.5px; color: #64748b;">Intet angivet</div>'
                }
              </div>
            </td>
            <td class="col-right">
              <div class="section-block">
                <div class="section-title">Motorspecifikationer</div>
                <table class="kv-table">
                  <tbody>
                    ${motorOrderedDefs
                      .filter((d) => d.val && String(d.val).trim())
                      .map(
                        (d) => `
                      <tr>
                        <td class="kv-label">${escapeHtml(d.label)}</td>
                        <td class="kv-val">${escapeHtml(d.val)}</td>
                      </tr>
                    `
                      )
                      .join('')}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>

          <!-- Row 3 Page 1: Stand/Bytte & Gear/Diverse/Sejludrustning -->
          <tr class="row-divider">
            <td class="col-left">
              <div class="section-block">
                <div class="section-title">Bådens stand</div>
                <div style="font-size: 8.5px; line-height: 1.35; color: #334155; margin-bottom: 8px;">
                  ${escapeHtml(baadStand || 'Almindelig pæn og velholdt båd')}
                </div>
                <div class="section-title">Kan evt. byttes med</div>
                <div style="font-size: 8.5px; line-height: 1.35; color: #334155;">
                  ${escapeHtml(baadBytte || 'Ingen bytte')}
                </div>
              </div>
            </td>
            <td class="col-right">
              <div class="section-block">
                <div class="section-title">Sejludrustning</div>
                ${
                  sailDataRows.length > 0
                    ? `
                  <table class="kv-table">
                    <tbody>
                      ${sailDataRows
                        .map(
                          (r) => `
                        <tr>
                          <td class="kv-label">${escapeHtml(r.label)}</td>
                          <td class="kv-val">${escapeHtml(r.value)}</td>
                        </tr>
                      `
                        )
                        .join('')}
                    </tbody>
                  </table>
                `
                    : '<div style="font-size: 8.5px; color: #64748b;">Ikke oplyst</div>'
                }
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Informationslinje -->
      <div class="info-bar">
        <div class="info-bar-top">Nordfyn Marine tager forbehold for evt. fejl i informationer.</div>
        <div class="info-bar-sub">Nordfyn Marine · Stationsvej 24 · 5450 Otterup · Telefon 40 20 70 34 · E-mail: info@nordfyn-marine.dk</div>
      </div>
    </div>

    <!-- ================================================== -->
    <!-- SIDE 2 -->
    <!-- ================================================== -->
    <div class="page ${!hasPage3 ? 'page-last' : ''}">
      <!-- Header Side 2 -->
      <div class="header-box">
        <div class="header-logo-container">
          <img src="${logoUrl}" class="header-logo" alt="Nordfyn Marine" />
        </div>
        <div class="header-title-container">
          <h1 class="header-main-title">Salgsopstilling fra Nordfyn Marine</h1>
          <div class="header-subtitle">Ring og aftal tid for besigtigelse på telefon 40 20 70 34 – svarer altid.</div>
        </div>
      </div>

      <!-- Billedgalleri - Side 2 (Adaptive Grid der fylder siden) -->
      ${renderGalleryHtml(page2Photos, !hasPage3)}

      ${
        !hasPage3
          ? `
        <!-- Afsluttende Salgsannonce Box (hvis kun 2 sider) -->
        <div class="sales-ad-box">
          <table class="sales-ad-table">
            <tbody>
              <tr>
                <td class="sales-ad-left">
                  <div class="ad-left-content">
                    <div class="ad-title">
                      Ring og få en snak om dine muligheder for salg af båd.
                    </div>
                    <div class="ad-bullets">
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Fast salærpris</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Ingen skjulte omkostninger</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Ingen markedsføringsomkostninger</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Intet startgebyr</div>
                    </div>
                  </div>
                </td>
                <td class="sales-ad-right">
                  <div class="ad-graphic">
                    <img src="${logoUrl}" class="ad-graphic-logo" alt="Nordfyn Marine" />
                    <div class="ad-graphic-title">SKAL VI SÆLGE DIN BÅD?</div>
                    <div class="ad-graphic-badge">SOLGT ELLER GRATIS</div>
                    <div class="ad-graphic-phone">Ring på tlf. 40 20 70 34</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      `
          : `
        <!-- Informationslinje Side 2 -->
        <div class="info-bar">
          <div class="info-bar-top">Nordfyn Marine tager forbehold for evt. fejl i informationer.</div>
          <div class="info-bar-sub">Nordfyn Marine · Stationsvej 24 · 5450 Otterup · Telefon 40 20 70 34 · E-mail: info@nordfyn-marine.dk</div>
        </div>
      `
      }
    </div>

    <!-- ================================================== -->
    <!-- SIDE 3 (kun hvis mere end 4 ekstra billeder) -->
    <!-- ================================================== -->
    ${
      hasPage3
        ? `
      <div class="page page-last">
        <!-- Header Side 3 -->
        <div class="header-box">
          <div class="header-logo-container">
            <img src="${logoUrl}" class="header-logo" alt="Nordfyn Marine" />
          </div>
          <div class="header-title-container">
            <h1 class="header-main-title">Salgsopstilling fra Nordfyn Marine</h1>
            <div class="header-subtitle">Ring og aftal tid for besigtigelse på telefon 40 20 70 34 – svarer altid.</div>
          </div>
        </div>

        <!-- Billedgalleri - Side 3 Fortsættelse -->
        ${renderGalleryHtml(page3Photos, true)}

        <!-- Afsluttende Salgsannonce Box -->
        <div class="sales-ad-box">
          <table class="sales-ad-table">
            <tbody>
              <tr>
                <td class="sales-ad-left">
                  <div class="ad-left-content">
                    <div class="ad-title">
                      Ring og få en snak om dine muligheder for salg af båd.
                    </div>
                    <div class="ad-bullets">
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Fast salærpris</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Ingen skjulte omkostninger</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Ingen markedsføringsomkostninger</div>
                      <div class="ad-bullet-item"><span class="ad-bullet-dot">✓</span> Intet startgebyr</div>
                    </div>
                  </div>
                </td>
                <td class="sales-ad-right">
                  <div class="ad-graphic">
                    <img src="${logoUrl}" class="ad-graphic-logo" alt="Nordfyn Marine" />
                    <div class="ad-graphic-title">SKAL VI SÆLGE DIN BÅD?</div>
                    <div class="ad-graphic-badge">SOLGT ELLER GRATIS</div>
                    <div class="ad-graphic-phone">Ring på tlf. 40 20 70 34</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    `
        : ''
    }
    `

    const htmlContent = `<!DOCTYPE html>
<html lang="da">
<head>
  <meta charset="UTF-8">
  <title>Salgsopstilling - ${escapeHtml(boat.title)} - Nordfyn Marine</title>
  <style>
    ${stylesCss}
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-info">
      <div class="toolbar-title">${mode === 'print' ? 'Udskriv salgsopstilling' : 'Salgsopstilling (PDF)'} – ${escapeHtml(boat.title)}</div>
      <div class="toolbar-hint">${mode === 'print' ? '💡 Vælg din printer i menuen for at udskrive.' : '💡 Vælg <strong>"Gem som PDF"</strong> under Destination i printermenuen for at downloade PDF-filen.'}</div>
    </div>
    <div class="toolbar-actions">
      <button class="btn-print" style="background:#e11d48;" onclick="window.print()">
        <span>Download PDF</span>
      </button>
      <button class="btn-print" onclick="window.print()">
        <span>Udskriv</span>
      </button>
      <button class="btn-close" onclick="window.close()">Luk</button>
    </div>
  </div>

  <div class="document-container">
    ${rawPagesHtml}
  </div>
  <script>
    function triggerPrint() {
      setTimeout(function() {
        try {
          window.focus();
          window.print();
        } catch (e) {
          console.error(e);
        }
      }, 350);
    }

    function cropBoatImagesForPrint() {
      try {
        var boatImgs = document.querySelectorAll('.main-boat-img, .gallery-img');
        boatImgs.forEach(function(img) {
          if (img.complete && img.naturalWidth && img.naturalHeight) {
            var wrapper = img.closest('.main-image-wrapper, .gallery-image-wrapper') || img.parentElement;
            var tw = wrapper ? (wrapper.clientWidth || wrapper.offsetWidth) : img.offsetWidth;
            var th = wrapper ? (wrapper.clientHeight || wrapper.offsetHeight) : img.offsetHeight;
            if (tw > 0 && th > 0) {
              var targetRatio = tw / th;
              var natRatio = img.naturalWidth / img.naturalHeight;
              var sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
              if (natRatio > targetRatio) {
                sw = Math.round(img.naturalHeight * targetRatio);
                sx = Math.round((img.naturalWidth - sw) / 2);
              } else {
                sh = Math.round(img.naturalWidth / targetRatio);
                sy = Math.round((img.naturalHeight - sh) / 2);
              }
              var c = document.createElement('canvas');
              c.width = Math.min(Math.max(tw * 2, 800), 2400);
              c.height = Math.round(c.width / targetRatio);
              var ctx = c.getContext('2d');
              if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
                img.src = c.toDataURL('image/jpeg', 0.95);
              }
            }
          }
        });
      } catch (e) {
        console.warn(e);
      }
    }

    var images = Array.from(document.querySelectorAll('img'));
    if (images.length === 0) {
      triggerPrint();
    } else {
      var loadedCount = 0;
      var totalImages = images.length;
      var hasTriggered = false;

      function checkAllLoaded() {
        loadedCount++;
        if (loadedCount >= totalImages && !hasTriggered) {
          hasTriggered = true;
          cropBoatImagesForPrint();
          triggerPrint();
        }
      }

      images.forEach(function(img) {
        if (img.complete && img.naturalHeight !== 0) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded);
        }
      });

      setTimeout(function() {
        if (!hasTriggered) {
          hasTriggered = true;
          cropBoatImagesForPrint();
          triggerPrint();
        }
      }, 3000);
    }
  </script>
</body>
</html>`

    return { htmlContent, rawPagesHtml, stylesCss, fileName }
  }

  // Direkte Download PDF funktion (genererer altid PDF dynamisk ud fra aktuelle data)
  const handleDownloadPDF = async () => {
    if (!boat || isDownloadingPDF) return
    setIsDownloadingPDF(true)

    const sagsnr = String(boat.sagsnummer || '').trim()
    const cleanTitle = (boat.title || 'baad')
      .replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_')
      .replace(/__+/g, '_')
    const fileName = sagsnr ? `Salgsopstilling-${sagsnr}-${cleanTitle}.pdf` : `Salgsopstilling-${cleanTitle}.pdf`

    let renderContainer = null
    try {
      const defaultLogo = 'https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/nordfyn-marine-logo.png'
      const rawExtra = galleryImages.slice(1)
      const rawP2 = rawExtra.length <= 4 ? rawExtra : rawExtra.slice(0, 4)
      const rawP3 = rawExtra.length > 4 ? rawExtra.slice(4, 8) : []

      const [convertedLogo, convertedMain, convertedP2, convertedP3] = await Promise.all([
        urlToDataUrl(defaultLogo),
        galleryImages[0] ? urlToDataUrl(galleryImages[0]) : Promise.resolve(''),
        Promise.all(rawP2.map((u) => urlToDataUrl(u))),
        Promise.all(rawP3.map((u) => urlToDataUrl(u)))
      ])

      console.log('=== PDF IMAGE DEBUG ===')
      console.log('Logo:', {
        success: convertedLogo.startsWith('data:image/'),
        length: convertedLogo.length
      })
      console.log('Main photo:', {
        original: galleryImages[0],
        success: convertedMain.startsWith('data:image/'),
        length: convertedMain.length
      })
      convertedP2.forEach((image, index) => {
        console.log(`Page 2 image ${index + 1}:`, {
          original: rawP2[index],
          success: image.startsWith('data:image/'),
          length: image.length
        })
      })
      convertedP3.forEach((image, index) => {
        console.log(`Page 3 image ${index + 1}:`, {
          original: rawP3[index],
          success: image.startsWith('data:image/'),
          length: image.length
        })
      })

      const imgOverrides = {
        logoUrl: convertedLogo || defaultLogo,
        mainPhoto: convertedMain,
        page2Photos: convertedP2.filter(Boolean),
        page3Photos: convertedP3.filter(Boolean)
      }

      const docData = buildSalesDocData('download', imgOverrides)
      if (!docData) throw new Error('Kunne ikke opbygge salgsopstilling')

      const { rawPagesHtml, stylesCss } = docData

      // Opret off-screen container direkte i document.body med synlig styling for html2canvas
      renderContainer = document.createElement('div')
      renderContainer.id = 'pdf-render-workspace'
      renderContainer.style.position = 'absolute'
      renderContainer.style.top = '0'
      renderContainer.style.left = '-10000px'
      renderContainer.style.width = '794px'
      renderContainer.style.background = '#ffffff'
      renderContainer.style.pointerEvents = 'none'
      renderContainer.style.zIndex = '1'
      renderContainer.style.opacity = '1'
      renderContainer.style.visibility = 'visible'
      renderContainer.innerHTML = `
        <style>
          ${stylesCss}
          .toolbar { display: none !important; }
          .document-container {
            margin: 0 !important;
            padding: 0 !important;
            width: 794px !important;
            background: #ffffff !important;
          }
          .page {
            width: 794px !important;
            height: 1123px !important;
            min-height: 1123px !important;
            max-height: 1123px !important;
            box-sizing: border-box !important;
            padding: 24px 28px !important;
            margin: 0 !important;
            background: #ffffff !important;
            border: none !important;
            box-shadow: none !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }
          #pdf-render-workspace .gallery-wrapper {
            flex: 1 !important;
            height: auto !important;
            min-height: 0 !important;
          }
        </style>
        <div class="document-container">
          ${rawPagesHtml}
        </div>
      `
      document.body.appendChild(renderContainer)

      // Vent på rigtig billed-dekodning via img.decode()
      const domImages = Array.from(renderContainer.querySelectorAll('img'))
      await Promise.all(
        domImages.map(async (img) => {
          try {
            if (!img.complete || img.naturalWidth === 0) {
              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error(`Timeout ved indlæsning af billede: ${img.src}`))
                }, 10000)
                img.onload = () => {
                  clearTimeout(timeout)
                  resolve()
                }
                img.onerror = () => {
                  clearTimeout(timeout)
                  reject(new Error(`Kunne ikke indlæse billede: ${img.src}`))
                }
              })
            }
            if (typeof img.decode === 'function') {
              await img.decode()
            }
            if (!img.naturalWidth || !img.naturalHeight) {
              throw new Error(`Billedet har ugyldige dimensioner: ${img.src}`)
            }
            console.log('PDF-billede klar:', {
              width: img.naturalWidth,
              height: img.naturalHeight,
              sourceType: img.src.startsWith('data:') ? 'data-url' : 'url'
            })
          } catch (error) {
            console.error('PDF-billede kunne ikke klargøres:', error)
          }
        })
      )

      // Beskær alle bådbilleder (forside og galleri) nøjagtigt til deres kasser, så html2canvas ikke forvrænger dem
      const boatImagesToCrop = Array.from(
        renderContainer.querySelectorAll('.main-boat-img, .gallery-img')
      )
      for (const img of boatImagesToCrop) {
        try {
          const wrapper = img.closest('.main-image-wrapper, .gallery-image-wrapper') || img.parentElement
          const targetW = wrapper ? (wrapper.clientWidth || wrapper.offsetWidth) : (img.clientWidth || img.offsetWidth)
          const targetH = wrapper ? (wrapper.clientHeight || wrapper.offsetHeight) : (img.clientHeight || img.offsetHeight)

          if (targetW > 0 && targetH > 0 && img.naturalWidth > 0 && img.naturalHeight > 0) {
            const croppedDataUrl = cropImageToCover(img, targetW, targetH)
            if (croppedDataUrl) {
              img.src = croppedDataUrl
              if (typeof img.decode === 'function') {
                await img.decode()
              }
            }
          }
        } catch (cropErr) {
          console.warn('Kunne ikke pre-croppe billede for PDF:', cropErr)
        }
      }

      // Vent på skrifttyper og reflow
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready
      }
      await new Promise((r) => setTimeout(r, 200))

      const pageElements = Array.from(renderContainer.querySelectorAll('.page'))
      if (pageElements.length === 0) {
        throw new Error('Ingen sider fundet til PDF-generering')
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      })

      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i]
        const canvas = await html2canvas(pageEl, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 794,
          scrollX: 0,
          scrollY: 0
        })

        const imgData = canvas.toDataURL('image/jpeg', 0.95)
        if (i > 0) {
          pdf.addPage('a4', 'portrait')
        }
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST')
      }

      pdf.save(fileName)
    } catch (err) {
      console.error('Fejl ved generering af PDF:', err)
      alert('PDF kunne ikke genereres korrekt. Se browserkonsollen for detaljer.')
    } finally {
      if (renderContainer && renderContainer.parentNode) {
        renderContainer.parentNode.removeChild(renderContainer)
      }
      setIsDownloadingPDF(false)
    }
  }

  // Udskriv funktion (åbner altid dynamisk salgsopstilling genereret ud fra de aktuelle data)
  const handlePrintPDF = async (mode = 'print') => {
    if (!boat) return

    // Åbn popup med det samme synkront for at undgå popup-blokker
    let printWindow = null
    try {
      printWindow = window.open('', '_blank', 'width=1000,height=900')
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head><title>Forbereder salgsopstilling...</title></head>
            <body style="font-family: Arial, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f8fafc; color: #022046;">
              <div style="text-align: center;">
                <div style="font-size: 20px; font-weight: bold; margin-bottom: 8px;">Forbereder salgsopstilling...</div>
                <div style="font-size: 14px; color: #64748b;">Henter billeder og klargør ${mode === 'download' ? 'PDF-download' : 'udskrift'}...</div>
              </div>
            </body>
          </html>
        `)
        printWindow.document.close()
      }
    } catch (e) {
      console.warn('Popup blocked or failed:', e)
    }

    let imgOverrides = null
    try {
      const defaultLogo = 'https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/nordfyn-marine-logo.png'
      const rawExtra = galleryImages.slice(1)
      const rawP2 = rawExtra.length <= 4 ? rawExtra : rawExtra.slice(0, 4)
      const rawP3 = rawExtra.length > 4 ? rawExtra.slice(4, 8) : []

      const [convertedLogo, convertedMain, convertedP2, convertedP3] = await Promise.all([
        urlToDataUrl(defaultLogo),
        galleryImages[0] ? urlToDataUrl(galleryImages[0]) : Promise.resolve(''),
        Promise.all(rawP2.map((u) => urlToDataUrl(u))),
        Promise.all(rawP3.map((u) => urlToDataUrl(u)))
      ])

      imgOverrides = {
        logoUrl: convertedLogo || defaultLogo,
        mainPhoto: convertedMain,
        page2Photos: convertedP2.filter(Boolean),
        page3Photos: convertedP3.filter(Boolean)
      }
    } catch (e) {
      console.warn('Kunne ikke forudkonvertere alle billeder til print:', e)
    }

    const docData = buildSalesDocData(mode, imgOverrides)
    if (!docData) return
    const { htmlContent } = docData

    if (printWindow && !printWindow.closed) {
      try {
        printWindow.document.open()
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        return
      } catch (err) {
        console.error('Error writing to print popup:', err)
      }
    }

    // Fallback if popup is blocked: use hidden iframe
    try {
      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '10px'
      iframe.style.height = '10px'
      iframe.style.opacity = '0.01'
      iframe.style.border = '0'
      iframe.style.zIndex = '-999'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentWindow.document
      iframeDoc.open()
      iframeDoc.write(htmlContent)
      iframeDoc.close()

      setTimeout(() => {
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
        } catch (e) {
          console.error('Iframe print error:', e)
        }
        setTimeout(() => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe)
          }
        }, 60000)
      }, 500)
    } catch (e) {
      console.error('Print iframe fallback failed:', e)
    }
  }

  // Handle Contact Form Submission
  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setFormStatus({ submitting: true, success: false, error: '' })

    // Basic validation
    if (!formState.navn.trim() || !formState.telefon.trim() || !formState.email.trim() || !formState.beskrivelse.trim()) {
      setFormStatus({ submitting: false, success: false, error: 'Udfyld venligst alle påkrævede felter.' })
      return
    }

    try {
      const payload = {
        navn: formState.navn,
        telefon: formState.telefon,
        email: formState.email,
        beskrivelse: formState.beskrivelse,
        baad: boat.title || '',
        website_url: formState.website_url // Honeypot
      }

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: '' })
        setFormState({ navn: '', telefon: '', email: '', beskrivelse: '', website_url: '' })
      } else {
        const data = await response.json().catch(() => ({}))
        setFormStatus({
          submitting: false,
          success: false,
          error: data.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen.'
        })
      }
    } catch (err) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Der opstod en netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      })
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-8">
      <SEO
        title={seoTitle}
        description={seoDesc}
        image={mainImg}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        type="product"
      />

      {/* Lightbox Modal */}
      <LightboxGallery
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={galleryImages}
        initialIndex={activeImageIndex}
      />

      {/* Share Boat Modal */}
      {shareModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setShareModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full border border-slate-200 relative animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShareModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Luk"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#1E7BC1]/10 flex items-center justify-center text-[#1E7BC1]">
                <i className="fa-solid fa-share-nodes text-lg" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Del bådannonce</h3>
                <p className="text-xs text-slate-500">Del linket direkte til sociale medier eller venner</p>
              </div>
            </div>

            {/* Social Preview Card */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 mb-5 flex gap-3 items-center">
              {mainImg && (
                <img
                  src={
                    mainImg.includes('assets.auxodev.dk')
                      ? mainImg.replace('assets.auxodev.dk/', 'assets.auxodev.dk/cdn-cgi/image/width=240,height=160,fit=cover,quality=80,format=auto/')
                      : mainImg
                  }
                  alt={boat.title}
                  className="w-24 h-16 object-cover rounded-lg shrink-0 border border-slate-200"
                />
              )}
              <div className="min-w-0 flex-1">
                <span className="text-[10px] uppercase font-bold text-[#1E7BC1] tracking-wider block">
                  {boatType || 'Båd til salg'} {boat.sagsnummer ? `· Sagsnr. ${boat.sagsnummer}` : ''}
                </span>
                <h4 className="font-bold text-sm text-slate-900 truncate">{boat.title}</h4>
                <p className="text-xs font-semibold text-emerald-700">{boat.pris || 'Kontakt for pris'}</p>
              </div>
            </div>

            {/* Direct Sharing Actions */}
            <div className="space-y-2 mb-5">
              <span className="text-xs font-semibold text-slate-700 block">Vælg hvor du vil dele:</span>
              
              <div className="grid grid-cols-2 gap-2">
                {/* Facebook */}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    typeof window !== 'undefined' ? window.location.href : ''
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-[#1877F2] text-white text-xs font-bold hover:bg-[#166fe5] transition-colors shadow-xs"
                >
                  <i className="fa-brands fa-facebook-f text-sm" />
                  <span>Facebook</span>
                </a>

                {/* WhatsApp */}
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                    `${boat.title} - ${typeof window !== 'undefined' ? window.location.href : ''}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-[#25D366] text-white text-xs font-bold hover:bg-[#20bd5a] transition-colors shadow-xs"
                >
                  <i className="fa-brands fa-whatsapp text-sm" />
                  <span>WhatsApp</span>
                </a>

                {/* Email */}
                <a
                  href={`mailto:?subject=${encodeURIComponent(`Båd til salg: ${boat.title}`)}&body=${encodeURIComponent(
                    `Hej,\n\nJeg vil gerne dele denne båd fra Nordfyn Marine med dig:\n\n${boat.title}\nPris: ${boat.pris || 'Kontakt for pris'}\n\nSe annoncen her: ${typeof window !== 'undefined' ? window.location.href : ''}`
                  )}`}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-slate-800 text-white text-xs font-bold hover:bg-slate-900 transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-envelope text-sm" />
                  <span>E-mail</span>
                </a>

                {/* Messenger / General web share */}
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: boat.title,
                        text: seoDesc,
                        url: window.location.href
                      }).catch(() => {})
                    } else {
                      handleCopyLink()
                    }
                  }}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-sky-100 text-sky-800 text-xs font-bold hover:bg-sky-200 transition-colors"
                >
                  <i className="fa-solid fa-arrow-up-from-bracket text-sm" />
                  <span>Flere apps</span>
                </button>
              </div>
            </div>

            {/* Copy Link Input Bar */}
            <div className="space-y-1.5 mb-4">
              <label className="text-xs font-semibold text-slate-700 block">Kopier direkte link:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={typeof window !== 'undefined' ? window.location.href : ''}
                  className="flex-1 text-xs bg-slate-100 border border-slate-300 rounded-xl px-3 py-2 text-slate-700 select-all outline-none focus:border-[#1E7BC1]"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-xl text-xs font-bold text-white transition-colors cursor-pointer shrink-0 flex items-center gap-1.5 ${
                    copiedLink ? 'bg-emerald-600' : 'bg-[#1E7BC1] hover:bg-[#2BABE3]'
                  }`}
                >
                  <i className={`fa-solid ${copiedLink ? 'fa-check' : 'fa-copy'}`} />
                  <span>{copiedLink ? 'Kopieret!' : 'Kopiér'}</span>
                </button>
              </div>
            </div>

            {/* Facebook Crawler / Cache helper note */}
            <div className="bg-sky-50/70 border border-sky-100 rounded-xl p-3 text-[11px] text-slate-600 flex items-start gap-2">
              <i className="fa-solid fa-circle-info text-[#1E7BC1] mt-0.5 shrink-0" />
              <div>
                <p>
                  Billedet der vises ved deling på Facebook, iMessage og WhatsApp er automatisk <strong>bådens 1. billede</strong>.
                </p>
                <a
                  href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(
                    typeof window !== 'undefined' ? window.location.href : ''
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1E7BC1] hover:underline font-semibold inline-flex items-center gap-1 mt-1"
                >
                  <span>Test eller ryd Facebooks cache her</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Generation Loading Backdrop */}
      {isDownloadingPDF && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-xs w-full text-center flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#1E7BC1]/10 flex items-center justify-center text-[#1E7BC1]">
              <i className="fa-solid fa-circle-notch fa-spin text-2xl" aria-hidden="true" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Genererer salgsopstilling</h3>
              <p className="text-xs text-slate-500 mt-1">
                Et øjeblik... Klargør PDF til download.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Breadcrumbs Navigation & Back Button */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <nav className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-[#1E7BC1] transition-colors">
              Forside
            </Link>
            <i className="fa-solid fa-chevron-right text-[9px]" />
            <Link to="/baade" className="hover:text-[#1E7BC1] transition-colors">
              Både
            </Link>
            {boatType && (
              <>
                <i className="fa-solid fa-chevron-right text-[9px]" />
                <span className="text-slate-700">{boatType}</span>
              </>
            )}
            <i className="fa-solid fa-chevron-right text-[9px]" />
            <span className="font-semibold text-slate-900 truncate max-w-[180px] sm:max-w-none">{boat.title}</span>
          </nav>

          <button
            type="button"
            onClick={handleGoBack}
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-[#1E7BC1] transition-colors bg-white hover:bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 shadow-xs cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-[11px]" />
            <span>Tilbage til oversigten</span>
          </button>
        </div>

        {/* 1. Product Name (Назва продукту) */}
        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {boatStatus && (
                <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#1E7BC1] text-white">
                  {boatStatus}
                </span>
              )}
              {boat.sagsnummer && (
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-900 border border-amber-200">
                  Sagsnr. {boat.sagsnummer}
                </span>
              )}
              {boatType && (
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-[#2BABE3]/10 text-[#1E7BC1] border border-[#2BABE3]/30">
                  {boatType}
                </span>
              )}
              <button
                type="button"
                onClick={handleShareClick}
                className="px-2.5 py-1 rounded-md text-xs font-bold bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors cursor-pointer flex items-center gap-1.5"
                title="Del eller kopier link til denne båd"
              >
                <i className="fa-solid fa-share-nodes text-[#1E7BC1]" />
                <span>Del båd</span>
              </button>
            </div>

            <h1 className="text-2xl sm:text-4xl font-serif font-bold text-slate-900">
              {boat.title}
            </h1>
            {boat.maerke_model && (
              <p className="text-xs text-slate-500 font-medium mt-1">
                {boat.maerke_model} {boat.aargang ? `· Årgang ${boat.aargang}` : ''}
              </p>
            )}
          </div>

          {/* 3. Price & Status Badge */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-left md:text-right shrink-0 min-w-[220px] flex flex-col justify-between gap-3">
            <div>
              <span className="text-[11px] text-slate-500 uppercase font-semibold block">Pris</span>
              <span className="font-serif font-bold text-2xl sm:text-3xl text-[#1E7BC1]">
                {boat.pris || 'Kontakt for pris'}
              </span>
            </div>
            <button
              type="button"
              onClick={handleDownloadPDF}
              disabled={isDownloadingPDF}
              title="Download salgsopstilling som PDF eller udskriv"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer hover:shadow-md active:scale-98 disabled:opacity-75 disabled:cursor-wait"
            >
              {isDownloadingPDF ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-white text-sm" aria-hidden="true" />
                  <span>Downloader PDF...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-file-arrow-down text-white text-sm" aria-hidden="true" />
                  <span>Download PDF / udskriv</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 2. Main Image (Головне зображення) */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          {galleryImages.length > 0 ? (
            <div
              onClick={() => openLightbox(0)}
              className="relative aspect-[16/10] sm:aspect-[21/9] max-h-[520px] rounded-xl overflow-hidden cursor-pointer group bg-slate-100"
            >
              <img
                src={galleryImages[0]}
                alt={boat.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-md text-white text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 pointer-events-none shadow-lg">
                <i className="fa-solid fa-expand" />
                <span>Åbn fotogalleri ({galleryImages.length} billeder)</span>
              </div>
            </div>
          ) : (
            <div className="aspect-[16/10] bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
              <span>Intet billede tilgængeligt</span>
            </div>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column (2 cols) */}
          <div className="lg:col-span-2 space-y-8">
            {/* 4. Beskrivelse */}
            {(() => {
              const parts = []
              if (boat.beskrivelse) {
                parts.push(boat.beskrivelse)
              } else if (boat.kort_beskrivelse) {
                parts.push(`<p>${boat.kort_beskrivelse}</p>`)
              }

              // Check if equipment is in separate field and not already inside beskrivelse
              const eqHtml = boat.udstyr_medfoelger || boat.udstyrsliste || boat.desuden_medfoelger || ''
              const cleanEq = eqHtml.replace(/<[^>]*>/g, '').trim()
              const descHasEquipment = boat.beskrivelse && boat.beskrivelse.match(/(?:udstyr\s+der\s+medfølger|desuden\s+medfølger|desuden\s+medfølge|medfølgende\s+udstyr|udstyrsliste)/i)

              if (cleanEq !== '' && !descHasEquipment) {
                parts.push(`<p><strong>Udstyr der medfølger:</strong></p>`)
                parts.push(eqHtml)
              }

              // Check if boat condition is in separate field and not already inside beskrivelse
              const standVal = boat.baadens_stand || boat.stand || ''
              const cleanStand = typeof standVal === 'string' ? standVal.replace(/<[^>]*>/g, '').trim() : ''
              const descHasStand = boat.beskrivelse && boat.beskrivelse.match(/(?:bådens\s+stand|båd\s+stand)/i)

              if (cleanStand !== '' && !descHasStand) {
                parts.push(`<p><strong>Bådens stand:</strong> ${cleanStand}</p>`)
              }

              // Check if trade-in is in separate field and not already inside beskrivelse
              const bytteVal = boat.kan_byttes_med || boat.bytte || boat.evt_bytte || ''
              const cleanBytte = typeof bytteVal === 'string' ? bytteVal.replace(/<[^>]*>/g, '').trim() : ''
              const descHasBytte = boat.beskrivelse && boat.beskrivelse.match(/(?:kan\s+evt\.?\s*byttes|evt\.?\s*bytte)/i)

              if (cleanBytte !== '' && !descHasBytte) {
                parts.push(`<p><strong>Kan evt. byttes med:</strong> ${cleanBytte}</p>`)
              }

              const fullHtml = parts.join('')
              if (!fullHtml.trim()) return null

              return (
                <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                      <i className="fa-solid fa-align-left text-sm" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                      Beskrivelse
                    </h2>
                  </div>

                  <div
                    className="prose max-w-none text-slate-700 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: fullHtml }}
                  />
                </div>
              )
            })()}

            {/* 5. Båddata */}
            {boatDataRows.length > 0 && (
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                    <i className="fa-solid fa-ship text-sm" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                    Båddata
                  </h2>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                        <th className="py-3 px-4 w-1/2">Parameter</th>
                        <th className="py-3 px-4 w-1/2">Værdi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {boatDataRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-700">{row.label}</td>
                          <td className="py-3 px-4 font-semibold text-slate-900">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 6. Motorspecifikationer */}
            {motorDataRows.length > 0 && (
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                    <i className="fa-solid fa-gears text-sm" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                    Motorspecifikationer
                  </h2>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                        <th className="py-3 px-4 w-1/3 sm:w-2/5">Parameter</th>
                        <th className="py-3 px-4 w-2/3 sm:w-3/5">Værdi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {motorDataRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-700 align-top">{row.label}</td>
                          <td className="py-3 px-4 font-semibold text-slate-900 break-words whitespace-pre-line align-top">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 7. Sejludrustning */}
            {sailDataRows.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-6">
                    <div className="w-8 h-8 rounded-lg bg-[#008097]/10 text-[#008097] flex items-center justify-center">
                      <i className="fa-solid fa-compass-drafting text-sm" />
                    </div>
                    <h2 className="text-2xl font-normal text-[#202b36] font-serif tracking-tight">
                      Sejludrustning
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 divide-y md:divide-y-0 divide-slate-200 text-base">
                    {sailDataRows.map((row, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center py-3.5 border-b border-slate-200"
                      >
                        <span className="font-semibold text-slate-800">{row.label}</span>
                        <span className="font-normal text-slate-700 text-right">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-3 w-full bg-[#008097]" />
              </div>
            )}

            {/* Ekstra specifikationer */}
            {extraRows.length > 0 && (
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                    <i className="fa-solid fa-list-check text-sm" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                    Ekstra specifikationer
                  </h2>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                        <th className="py-3 px-4 w-1/2">Parameter</th>
                        <th className="py-3 px-4 w-1/2">Værdi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {extraRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-700">{row.label}</td>
                          <td className="py-3 px-4 font-semibold text-slate-900">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 8. Øvrige oplysninger */}
            {otherDataItems.length > 0 && (
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                    <i className="fa-solid fa-circle-info text-sm" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                    Øvrige oplysninger
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {otherDataItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-50 p-4 border border-slate-200 rounded-xl flex items-start gap-3"
                    >
                      <i className={`${item.icon} text-[#1E7BC1] text-base mt-0.5 shrink-0`} />
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block">
                          {item.label}
                        </span>
                        <span className="text-sm font-bold text-slate-900">
                          {item.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 9. Full Photo Gallery (Повна фотогалерея) */}
            {galleryImages.length > 0 && (
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                      <i className="fa-solid fa-images text-sm" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                      Fotogalleri ({galleryImages.length})
                    </h2>
                  </div>
                  <span className="text-xs text-slate-500">Klik for at forstørre</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {galleryImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => openLightbox(idx)}
                      className="relative aspect-square rounded-xl overflow-hidden cursor-pointer border border-slate-200 hover:border-[#1E7BC1] transition-all group bg-slate-100"
                    >
                      <img
                        src={img}
                        alt={`${boat.title} billede ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 10. Contact Form (Контактна форма) */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                <div className="w-8 h-8 rounded-lg bg-[#2BABE3]/10 text-[#1E7BC1] flex items-center justify-center">
                  <i className="fa-solid fa-paper-plane text-sm" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                    Kontakt os om denne båd
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Udfyld formularen nedenfor for at indhente mere information eller aftale fremvisning.
                  </p>
                </div>
              </div>

              {formStatus.success ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl text-center space-y-3">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-xl">
                    <i className="fa-solid fa-check" />
                  </div>
                  <h3 className="font-serif font-bold text-lg text-emerald-900">
                    Mange tak for din henvendelse!
                  </h3>
                  <p className="text-xs text-emerald-700">
                    Vi har modtaget din besked angående {boat.title} og vender tilbage hurtigst muligt.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormStatus({ submitting: false, success: false, error: '' })}
                    className="inline-block mt-2 text-xs font-bold text-[#1E7BC1] underline"
                  >
                    Send en anden besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Antispam Honeypot */}
                  <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                    <label htmlFor="website_url">Bypass</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formState.website_url}
                      onChange={(e) => setFormState({ ...formState, website_url: e.target.value })}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {formStatus.error && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-xl text-xs flex items-center gap-2">
                      <i className="fa-solid fa-circle-exclamation shrink-0 text-base" />
                      <span>{formStatus.error}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Navn */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formState.navn}
                        onChange={(e) => setFormState({ ...formState, navn: e.target.value })}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-xs focus:ring-2 focus:ring-[#1E7BC1] focus:border-[#1E7BC1] outline-none transition-all"
                      />
                    </div>

                    {/* Telefonnummer */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Telefonnummer <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formState.telefon}
                        onChange={(e) => setFormState({ ...formState, telefon: e.target.value })}
                        placeholder="Tlf. nr."
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-xs focus:ring-2 focus:ring-[#1E7BC1] focus:border-[#1E7BC1] outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      placeholder="Din e-mailadresse"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-xs focus:ring-2 focus:ring-[#1E7BC1] focus:border-[#1E7BC1] outline-none transition-all"
                    />
                  </div>

                  {/* Beskrivelse */}
                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Beskrivelse / Spørgsmål <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      required
                      value={formState.beskrivelse}
                      onChange={(e) => setFormState({ ...formState, beskrivelse: e.target.value })}
                      placeholder={`Skriv din forespørgsel vedrørende ${boat.title}...`}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-xs focus:ring-2 focus:ring-[#1E7BC1] focus:border-[#1E7BC1] outline-none transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus.submitting}
                    className="w-full sm:w-auto bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-8 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-50 shadow-md flex items-center justify-center gap-2"
                  >
                    {formStatus.submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        <span>Send forespørgsel</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right Column (1 col): Contact Sticky Card */}
          <div className="space-y-6">
            <div className="bg-[#1E7BC1] text-white p-6 rounded-2xl border border-blue-700 shadow-xl space-y-6 sticky top-28 md:top-32 [@media(min-width:1200px)]:top-44">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#2BABE3]">
                  Hurtig kontakt
                </span>
                <h3 className="font-serif font-bold text-2xl text-white mt-1">
                  Nordfyn Marine
                </h3>
                <p className="text-xs text-blue-100 mt-2 leading-relaxed">
                  Har du spørgsmål eller ønsker du en uforpligtende fremvisning af {boat.title}?
                </p>
              </div>

              <div className="space-y-3">
                <a
                  href="tel:40207034"
                  className="flex items-center justify-center gap-2 bg-[#2BABE3] hover:bg-white hover:text-[#1E7BC1] text-white py-3 px-4 rounded-xl font-bold text-xs transition-all shadow-md whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring på 40 20 70 34</span>
                </a>

                <a
                  href={`mailto:info@nordfyn-marine.dk?subject=Foresp%C3%B8rgsel%20p%C3%A5%20${encodeURIComponent(
                    boat.title || ''
                  )}`}
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white hover:text-[#1E7BC1] text-white py-3 px-4 rounded-xl font-bold text-xs transition-all border border-white/30 shadow-md whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-[#2BABE3]" aria-hidden="true" />
                  <span className="whitespace-nowrap">info@nordfyn-marine.dk</span>
                </a>

                <div className="flex flex-col gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleDownloadPDF}
                    disabled={isDownloadingPDF}
                    className="w-full flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#1E7BC1] py-3 px-4 rounded-xl font-bold text-xs transition-all shadow-md cursor-pointer group disabled:opacity-75 disabled:cursor-wait"
                  >
                    {isDownloadingPDF ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin text-[#1E7BC1]" aria-hidden="true" />
                        <span>Downloader PDF...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-file-arrow-down text-rose-500" aria-hidden="true" />
                        <span>Download PDF</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handlePrintPDF('print')}
                    className="w-full flex items-center justify-center gap-2 bg-white/20 hover:bg-white hover:text-[#1E7BC1] text-white py-3 px-4 rounded-xl font-bold text-xs transition-all border border-white/30 shadow-md cursor-pointer group"
                  >
                    <i className="fa-solid fa-print text-white group-hover:text-[#1E7BC1]" aria-hidden="true" />
                    <span>Udskriv</span>
                  </button>
                </div>
              </div>

              <div className="bg-blue-950/60 p-4 rounded-xl border border-blue-400/20 space-y-2 text-xs text-blue-100">
                {boat.aargang && (
                  <div className="flex justify-between border-b border-blue-800/60 pb-1.5">
                    <span className="text-blue-200">Årgang:</span>
                    <span className="font-semibold text-white">{boat.aargang}</span>
                  </div>
                )}
                {boatType && (
                  <div className="flex justify-between border-b border-blue-800/60 pb-1.5">
                    <span className="text-blue-200">Bådtype:</span>
                    <span className="font-semibold text-white">{boatType}</span>
                  </div>
                )}
                <div className="flex justify-between pb-0.5">
                  <span className="text-blue-200">Placering:</span>
                  <span className="font-semibold text-white">Kontakt for info</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Boats */}
        {relatedBoats.length > 0 && (
          <div className="pt-12 border-t border-slate-200 space-y-6">
            <div className="flex justify-between items-end">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#2BABE3]">
                  Lignende både
                </span>
                <h2 className="text-2xl font-serif font-bold text-slate-900 mt-0.5">
                  Se andre både i samme kategori
                </h2>
              </div>
              <Link
                to="/baade"
                className="text-xs font-bold text-[#1E7BC1] hover:text-[#2BABE3] transition-colors"
              >
                Se alle både
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedBoats.map((b) => (
                <BoatCard key={b.id || b.url_slug} boat={b} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
